/*
 DESCRIPTION:

 Cloudflare Turnstile for a [data-turnstile] container (see _includes/forms/turnstile.twig).
 Cloudflare's script is loaded the first time any widget is rendered, so pages where nobody interacts with a form never load it.

 const turnstile = Turnstile(container, { onChange: hasToken => ... }); // onChange fires whenever a token is set or cleared
 turnstile.render(); // Loads the script and renders the widget (idempotent)
 turnstile.hasToken(); // Whether the widget currently has a valid token
 turnstile.getToken().then(token => ...); // Renders if needed, and resolves once the widget has a token
 turnstile.reset(); // Get a new token – call after every submission, as a token can only be verified once

 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

const loadScript = () => {
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                scriptPromise = null;
                script.remove();
                reject(new Error('Failed to load Turnstile'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

export default (container, { onChange = () => {} } = {}) => {

    const { sitekey, action } = container.dataset;

    let renderPromise = null;
    let resizeObserver = null;
    let widgetId = null;
    let token = null;
    let pending = [];

    const setToken = value => {
        token = value;
        onChange(!!token);
    };

    const settle = (error = null) => {
        const callbacks = pending;
        pending = [];
        callbacks.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
    };

    const render = () => {
        if (!renderPromise) {
            renderPromise = loadScript()
                .then(turnstile => {
                    widgetId = turnstile.render(container, {
                        sitekey,
                        action,
                        appearance: 'interaction-only',
                        callback(value) {
                            setToken(value);
                            settle();
                        },
                        'expired-callback': () => {
                            setToken(null);
                        },
                        'error-callback': code => {
                            setToken(null);
                            settle(new Error(`Turnstile error ${code}`));
                            // Returning true tells Turnstile the error has been handled
                            return true;
                        }
                    });
                    // The widget is invisible unless Cloudflare wants an interaction, and then stays visible for a while
                    // after it's solved – so flag whether it actually takes up space, rather than going by its callbacks
                    resizeObserver = new ResizeObserver(() => {
                        container.toggleAttribute('data-visible', container.offsetHeight > 0);
                    });
                    resizeObserver.observe(container);
                })
                .catch(error => {
                    renderPromise = null;
                    throw error;
                });
        }
        return renderPromise;
    };

    const getToken = () => render().then(() => {
        if (token) {
            return token;
        }
        return new Promise((resolve, reject) => {
            pending.push({ resolve, reject });
        });
    });

    const hasToken = () => !!token;

    const reset = () => {
        setToken(null);
        if (widgetId !== null) {
            window.turnstile.reset(widgetId);
        }
    };

    const destroy = () => {
        settle(new Error('Turnstile widget removed'));
        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        }
        if (widgetId !== null) {
            window.turnstile.remove(widgetId);
            widgetId = null;
        }
        renderPromise = null;
        token = null;
    };

    return {
        render,
        hasToken,
        getToken,
        reset,
        destroy
    };
};
